import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Building2, FileDown, Globe2, ListChecks, RefreshCw, ShieldAlert, ShieldCheck } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { API, api, fmtTime, fmtUSD, fmtVal, withPersona } from "@/lib/api";
import { Progress } from "@/components/grc/RemediationPlan";
import { PilotExport } from "@/components/monitor/Reports";

const REC = { Approve: "text-emerald-700", "Approve with conditions": "text-amber-700", Decline: "text-red-700" };

function Kpi({ id, label, value, sub, tone }) {
  const c = { danger: "bg-red-500", warn: "bg-amber-400", ok: "bg-emerald-500" }[tone] || "bg-sky-500";
  return (
    <div className="panel panel-hover p-4 relative overflow-hidden">
      <div className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${c}`} /><span className="eyebrow">{label}</span></div>
      <div className="num text-2xl font-semibold text-slate-900 mt-2" data-testid={`exec-kpi-${id}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
      <div className={`absolute inset-x-0 top-0 h-0.5 ${c} opacity-70`} />
    </div>
  );
}

function Card({ icon: Icon, title, to, children, id }) {
  const nav = useNavigate();
  return (
    <div className="panel flex flex-col min-w-0" data-testid={id}>
      <button onClick={() => nav(to)} className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between group">
        <span className="text-sm font-semibold text-slate-900 flex items-center gap-2.5"><span className="icon-chip w-7 h-7 rounded-lg"><Icon className="w-3.5 h-3.5" /></span>{title}</span>
        <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-sky-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </button>
      <div className="p-5 flex-1 min-w-0 overflow-x-auto thin-scroll">{children}</div>
    </div>
  );
}

export default function Executive() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/executive").then((r) => setD(r.data)), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (!d) return <div className="flex-1 p-8 text-sm text-slate-500">Loading executive summary…</div>;
  const L = d.lending, A = d.aml, C = d.compliance, G = d.grc;
  return (
    <div className="flex-1 min-w-0 overflow-y-auto thin-scroll grid-bg" data-testid="executive-page">
      <div className="px-8 py-8 space-y-6 max-w-[1600px]">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="chip border-sky-200 bg-sky-50 text-sky-700 mb-3"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" />Leadership view</div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight hero-title pb-1">Executive summary</h1>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="exec-refresh" onClick={load} className="btn-ghost num text-[11px] whitespace-nowrap"><RefreshCw className="w-3 h-3" />As of {fmtTime(d.generated_at)}</button>
            <button data-testid="exec-board-report" onClick={() => window.open(withPersona(`${API}/executive/board-report.pdf`), "_blank")} className="btn-primary"><FileDown className="w-3.5 h-3.5" />Board report PDF</button>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-3 stagger">
          <Kpi id="pipeline" label="Loan pipeline" value={fmtUSD(L.requested, true)} sub={`${L.loans.length} loans · ${L.decided} decided`} />
          <Kpi id="recommended" label="System-sized exposure" value={fmtUSD(L.recommended, true)} sub={`${L.conditions} with conditions`} tone="ok" />
          <Kpi id="aml-open" label="Open AML alerts" value={A.open} sub={`${A.high} high severity`} tone="danger" />
          <Kpi id="aml-hold" label="Value on AML hold" value={fmtUSD(A.on_hold_value, true)} sub={`${fmtUSD(A.review_value, true)} under review`} tone="warn" />
          <Kpi id="gaps" label="Compliance gaps" value={C.pending + C.accepted} sub={`${C.pending} pending sign-off · ${C.accepted} accepted`} tone="warn" />
          <Kpi id="grc" label="Open GRC issues" value={G.open_issues} sub={`${G.high_open} high · ${G.overdue} overdue`} tone={G.overdue ? "danger" : "warn"} />
          <Kpi id="remediation" label="Remediation progress" value={`${G.remediation_pct}%`} sub={`${G.steps_done}/${G.steps_total} steps · ${G.overdue_steps} overdue`} tone={G.overdue_steps ? "danger" : "ok"} />
        </div>
        <div className="grid xl:grid-cols-[1.25fr_1fr_1fr] gap-4">
          <Card id="exec-lending" icon={Building2} title="Loan pipeline" to="/lending">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-500">{["Loan", "Requested", "LTV", "DSCR", "System", "Committee"].map((h) => <th key={h} className="pb-2 pr-3 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {L.loans.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-sky-50/40" data-testid={`exec-loan-${l.loan_key}`}>
                    <td className="py-2.5 pr-3 text-slate-900 font-medium whitespace-nowrap">{l.title}</td><td className="py-2.5 pr-3 num">{fmtUSD(l.requested, true)}</td>
                    <td className="py-2.5 pr-3 num">{fmtVal(l.ltv, "pct")}</td><td className="py-2.5 pr-3 num">{fmtVal(l.dscr, "x")}</td>
                    <td className={`py-2.5 pr-3 whitespace-nowrap ${REC[l.recommendation] || "text-slate-400"}`}>{l.recommendation || "Pending"}</td><td className="py-2.5 pr-3 text-slate-700">{l.decision || "-"}</td>
                  </tr>
                ))}
                {!L.loans.length && <tr><td colSpan={6} className="py-4 text-slate-500">No loans in underwriting.</td></tr>}
              </tbody>
            </table>
          </Card>
          <Card id="exec-aml" icon={ShieldAlert} title="AML exposure" to="/task/task-wire-ops">
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={A.by_rule} layout="vertical" margin={{ left: 0, right: 8 }}>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="rule" width={170} fontSize={10} stroke="#64748b" tickLine={false} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #cbd5e1", fontSize: 12 }} />
                  <Bar dataKey="count" fill="#0284c7" radius={[0, 6, 6, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="eyebrow mt-2 mb-1">Largest open alerts</div>
            {A.top.map((a) => <div key={a.id} className="flex justify-between text-xs py-1 border-t border-slate-100"><span className="text-slate-700 truncate">{a.id} · {a.wire.counterparty}</span><span className="num text-slate-900">{fmtUSD(a.wire.amount, true)}</span></div>)}
          </Card>
          <Card id="exec-compliance" icon={Globe2} title="Cross-border compliance" to="/task/task-compliance">
            {!C.by_jurisdiction.length && <div className="text-xs text-slate-500">No gap analysis has been run yet.</div>}
            {C.by_jurisdiction.map((j) => (
              <div key={j.code} className="py-2 border-t border-slate-100 first:border-t-0" data-testid={`exec-jur-${j.code}`}>
                <div className="flex justify-between text-sm"><span className="text-slate-900">US vs {j.jurisdiction}</span><span className="num text-xs text-slate-500">{j.total} gaps</span></div>
                <div className="flex h-2 rounded-full overflow-hidden mt-1.5 bg-slate-100">
                  <div className="bg-emerald-500" style={{ width: `${(j.accepted / j.total) * 100}%` }} />
                  <div className="bg-amber-400" style={{ width: `${(j.pending / j.total) * 100}%` }} />
                </div>
                <div className="text-[10px] num text-slate-500 mt-1">{j.accepted} accepted · {j.pending} pending · {j.high} high severity</div>
              </div>
            ))}
          </Card>
        </div>
        <Card id="exec-remediation" icon={ListChecks} title="Remediation progress" to="/grc">
          {!d.remediation.length && <div className="text-xs text-slate-500">No active remediation plans.</div>}
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
            {d.remediation.map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-xs py-1.5 border-t border-slate-100" data-testid={`exec-remediation-${r.id}`}>
                <span className="num text-slate-500 w-20 shrink-0">{r.id}</span>
                <span className="flex-1 text-slate-800 truncate" title={r.title}>{r.title}<span className="block text-[10px] text-slate-500">{r.owner} · target {r.due_date || "-"}</span></span>
                <Progress p={r.progress} id={`exec-${r.id}`} />
              </div>
            ))}
          </div>
        </Card>
        <Card id="exec-grc" icon={ShieldCheck} title="Top risks & access control" to="/grc">
          <div className="grid lg:grid-cols-[1fr_260px] gap-4">
            <div className="space-y-1.5">
              {d.top_issues.map((i) => <div key={i.id} className="flex items-start gap-2 text-xs"><AlertTriangle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" /><span className="num text-slate-500">{i.id}</span><span className="text-slate-800 flex-1">{i.title}</span><span className="text-slate-500">{i.owner}</span></div>)}
            </div>
            <div className="text-xs space-y-1 text-slate-700 border-l border-slate-200 pl-4">
              <div>Blocked access attempts: <span className="num text-red-600">{G.denied_attempts}</span></div>
              <div>Access reviews certified: <span className="num">{G.certified}/{G.reviews_total}</span></div>
            </div>
          </div>
        </Card>
        <PilotExport />
      </div>
    </div>
  );
}
